import React, { useEffect, useState } from "react";
import { Button, Pagination, Skeleton, Snackbar } from "@mui/material";
import DeleteModal from "@/components/DeleteModal/DeleteModal";
import { IMedia } from "@/store/models/video.models";
import EditVideoModal from "../EditVideoModal/EditVideoModal";
import AddVideoModal from "../AddVideoModal/AddVideoModal";
import { InputText } from "@/components/InputText/InputText";
import searchIcon from "@/assets/icons/search.svg";
import { useForm } from "react-hook-form";
import styles from "./VideoContent.module.scss";
import useItemsPerPage from "@/hooks/useItemsPerPage";
import { VideoCard } from "@/components/VideoCard/VideoCard";
import {
  useDeleteMediaMutation,
  useGetMediaVideoQuery,
  useUpdateMediaMutation,
} from "@/store/rtk-api/media.api";
import {
  useGetJobsPendingQuery,
  useUploadVideoMutation,
} from "@/store/rtk-api/media-upload.api";
import PublishModal from "@/components/PublishModal/PublishModal";

const VideoContent = () => {
  const [page, setPage] = useState(1);
  const [isOpenEditModal, setIsOpenEditModal] = useState(false);
  const [isOpenDeleteModal, setIsOpenDeleteModal] = useState(false);
  const [isOpenPublishModal, setIsOpenPublishModal] = useState(false);
  const [selectedVideo, setSelectedVideo] = useState<IMedia | null>(null);
  const [isOpenAddItemModal, setIsOpenAddItemModal] = useState(false);
  const [jobUid, setJobUid] = useState<number | null>(null);
  const [videosQuantity, setVideosQuantity] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);

  const itemsPerPage = useItemsPerPage(2, 2, 3, 1280, 1680);
  const { control, watch } = useForm();
  const searchInput = watch("search");

  const {
    data: videoData,
    refetch: refetchVideos,
    isLoading,
    isSuccess,
  } = useGetMediaVideoQuery({
    page: page,
    limit: itemsPerPage,
    sortByDate: "DESC",
    searchTerm: searchInput,
    isPublished: "none",
  });

  const { data: jobsPending, refetch: refetchJobsPending } =
    useGetJobsPendingQuery();

  const [deleteVideoByUid] = useDeleteMediaMutation();
  const [editVideo] = useUpdateMediaMutation();
  const [createVideo] = useUploadVideoMutation();

  useEffect(() => {
    if (jobsPending?.length > 0) {
      const intervalId = setInterval(() => {
        refetchJobsPending();
      }, 20000);

      return () => clearInterval(intervalId);
    } else if (jobsPending?.length === 0) {
      refetchVideos();
      setJobUid(null);
    }
  }, [jobsPending, refetchJobsPending, refetchVideos]);

  const handlePageChange = (
    event: React.ChangeEvent<unknown>,
    value: number
  ) => {
    setPage(value);
  };

  const handleToggleEditModal = (video: IMedia) => {
    setSelectedVideo(video);
    setIsOpenEditModal(true);
  };

  const handleToggleDeleteModal = (video: IMedia) => {
    setSelectedVideo(video);
    setIsOpenDeleteModal(true);
  };

  const handleTogglePublishModal = (video: IMedia) => {
    setSelectedVideo(video);
    setIsOpenPublishModal(true);
  };

  const handlePublishVideo = async () => {
    if (selectedVideo) {
      const formData = new FormData();
      formData.append("uid", selectedVideo.uid);
      if (selectedVideo.isPublished !== undefined) {
        formData.append("isPublished", String(!selectedVideo.isPublished));
      }

      await editVideo(formData);
      setIsOpenPublishModal(false);
      refetchVideos();
    }
  };

  const handleDeleteVideo = async () => {
    if (selectedVideo) {
      await deleteVideoByUid(selectedVideo.uid);
      setIsOpenDeleteModal(false);
      refetchVideos();
    }
  };

  const handleEditVideo = async (formData: FormData) => {
    await editVideo(formData);
    setIsOpenEditModal(false);
    refetchVideos();
  };

  const handleAddVideo = async (formData: FormData) => {
    try {
      const response = await createVideo(formData).unwrap();
      setJobUid(response.id);
    } catch (err) {
      setError("Failed to upload video. Please try again.");
      setSnackbarOpen(true);
    }
  };

  const handleToggleModal = () => {
    setIsOpenAddItemModal((prev) => !prev);
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setPage(1);
  };

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
    setError(null);
  };

  useEffect(() => {
    let singleVideos = 0;
    let multiplyVideo = 0;
    if (jobsPending?.length > 0) {
      jobsPending.forEach((job: any) => {
        if (job.name === "single-video-upload") {
          singleVideos += job.progress.total;
        } else if (job.name === "multiple-media-upload") {
          multiplyVideo += job.data.videos.length;
        }
      });
      setVideosQuantity(singleVideos + multiplyVideo);
    }
  }, [jobsPending]);

  return (
    <>
      <div className={styles.container}>
        <div className={styles.topSection}>
          <Button
            sx={{
              textTransform: "capitalize",
              height: "44px",
              width: "174px",
              backgroundColor: "#008000",
              borderRadius: "12px",

              "&:hover": {
                backgroundColor: "#008000",
              },
            }}
            variant="contained"
            onClick={handleToggleModal}
          >
            Add item
          </Button>
          <InputText
            label="Search"
            control={control}
            name="search"
            icon={searchIcon}
            onChange={handleSearchChange}
          />
        </div>

        <div className={styles.itemsContainer}>
          {(jobsPending?.length > 0 || jobUid !== null) && (
            <VideoCard
              video={{
                title: `${videosQuantity} file(s) are being uploaded...`,
                description: "",
                uid: Math.random().toString(),
                url: "",
                accessType: "",
                isFavourite: false,
                isPublished: false,
                likes: 0,
                subtitleUrl: "",
                thumbnailPrompt: "",
                thumbnailUrl: "",
                type: "",
                viewsCount: 0,
                sections: [],
              }}
              isLoading={true}
              isEdit={false}
            />
          )}
          {isLoading &&
            Array(3)
              .fill(0)
              .map((_, index) => (
                <div className={styles.item} key={index}>
                  <Skeleton
                    variant="rounded"
                    width="100%"
                    height="200px"
                    className={styles.skeletonItem}
                  />
                  <Skeleton
                    width="60%"
                    height="30px"
                    className={styles.skeletonItem}
                  />
                  <Skeleton
                    width="100%"
                    height="100px"
                    className={styles.skeletonItem}
                  />
                </div>
              ))}
          {isSuccess && videoData.total > 0 ? (
            videoData.media.map((video) => (
              <VideoCard
                key={video.uid}
                video={video}
                handleToggleEditModal={handleToggleEditModal}
                handleToggleDeleteModal={handleToggleDeleteModal}
                handleTogglePublishModal={handleTogglePublishModal}
                isEdit={true}
              />
            ))
          ) : (
            <div className={styles.noDataText}>No data yet</div>
          )}
        </div>
      </div>
      {isOpenEditModal && (
        <EditVideoModal
          isOpenEditModal={isOpenEditModal}
          setIsOpenEditModal={setIsOpenEditModal}
          selectedVideo={selectedVideo}
          setSelectedVideo={setSelectedVideo}
          handleEditVideo={handleEditVideo}
        />
      )}

      {isOpenDeleteModal && (
        <DeleteModal
          isOpen={isOpenDeleteModal}
          setIsOpen={setIsOpenDeleteModal}
          name={selectedVideo ? selectedVideo.title : ""}
          supportingText="Are you sure you want to delete this video?"
          onDelete={handleDeleteVideo}
        />
      )}

      {isOpenPublishModal && (
        <PublishModal
          isOpen={isOpenPublishModal}
          setIsOpen={setIsOpenPublishModal}
          name={selectedVideo ? selectedVideo.title : ""}
          isPublished={selectedVideo?.isPublished ?? true}
          onHandle={handlePublishVideo}
        />
      )}

      {isOpenAddItemModal && (
        <AddVideoModal
          isOpenAddItemModal={isOpenAddItemModal}
          setIsOpenAddItemModal={setIsOpenAddItemModal}
          onAddVideo={handleAddVideo}
        />
      )}
      {isSuccess && (
        <Pagination
          page={page}
          onChange={handlePageChange}
          count={Math.ceil(videoData.total / itemsPerPage)}
        />
      )}

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        message={error}
      />
    </>
  );
};

export default VideoContent;
